// src/pages/markets/board/card/body/ViewBody.tsx
//
// Two blocks now, not three: the ladder (which carries per-market conviction in
// its capital column) and the one-line conviction row for the selected market.
// CapitalBlock / SkillBlock / ScoreBlock are gone — their numbers live in the
// row, and their trader tables live in the row's hover panel.

import { useMemo } from 'react';
import { Box } from '@mui/material';
import type { BoardScore, MarketSeries, ScreenerEvent } from '../../api/boardTypes';
import type { CardVariant } from '../../board/columnConfig';
import { MarketBlock } from './MarketBlock';
import { ConvictionRow } from './ConvictionRow';
import { NewBlock } from './NewBlock';
import { ActivityBlock, type ActivityContext } from '../ActivityBlock';
import {
    ACTIVITY_MARKET_BLOCK_HEIGHT,
    BLOCK_GAP,
    MARKET_BLOCK_HEIGHT,
    META_MARKET_BLOCK_HEIGHT,
} from '../cardLayout';
import { useEventOutcomes } from '../../api/useEventOutcomes';
import { needsTrueOutcomes } from '../../utils/boardOutcomeLabels';

export interface ViewBodyProps {
    event: ScreenerEvent;
    score: BoardScore | null;
    // Scores of the active group across the event's markets — the ladder's
    // capital column.
    groupScores: BoardScore[];
    selectedMarketId: string | null;
    scoreEmptyText: string;
    onSelectMarket: (marketId: string) => void;
    variant?: CardVariant;
    now?: number;
    // Every score of the event, across groups — the New body asks "is anyone
    // tracked in here at all", which must not depend on the active group.
    allScores?: BoardScore[];
    activeTags?: readonly string[] | null;
    accentColor?: string;
    activityCtx?: ActivityContext | null;
    activityMarketLabel?: string | null;
    /** Price history for the selected market; the single-market body draws it.
     *  undefined means none was asked for, null means asked and there is none. */
    series?: MarketSeries | null;
    seriesWindow?: string;
    /** Overrides the block frame when the body has no price line to draw. */
    marketBlockHeight?: number;
}

const PENDING_ACTIVITY: ActivityContext = {
    activity: null,
    unit: '',
    metricMode: 'absolute',
    windows: { drift: '24h', jump: '15m' },
};

export function ViewBody({
    event,
    score,
    groupScores,
    selectedMarketId,
    scoreEmptyText,
    onSelectMarket,
    variant,
    now,
    allScores,
    activeTags,
    accentColor,
    activityCtx,
    activityMarketLabel,
    series,
    seriesWindow,
    marketBlockHeight,
}: ViewBodyProps) {
    const v = variant ?? 'score';
    const t = now ?? Date.now();

    // Only a head-to-head hides its yes outcome from the board payload; every
    // other shape is nameable without a request, so only that one pays for it.
    // Asked of the whole event, not the selected market: the ladder names every
    // row, and the answer must not flip as the reader steps through them.
    const needsOutcomes = v !== 'meta' && event.markets.some(needsTrueOutcomes);
    const outcomesByMarket = useEventOutcomes(event.event_id, needsOutcomes);
    const outcomes = score ? outcomesByMarket.get(score.market_id) ?? null : null;

    const scoreByMarket = useMemo(
        () => new Map(groupScores.map(s => [s.market_id, s])),
        [groupScores],
    );

    if (v === 'meta') {
        return (
            <NewBlock
                event={event}
                scores={allScores ?? (score ? [score] : [])}
                activeTags={activeTags}
                accentColor={accentColor}
                marketBlockHeight={marketBlockHeight ?? META_MARKET_BLOCK_HEIGHT}
                selectedMarketId={selectedMarketId}
                onSelectMarket={onSelectMarket}
                now={t}
            />
        );
    }

    // Without a context the column is mounted but the overlay has not answered
    // yet. The block still renders — at its fixed height, empty — so the ladder
    // above it does not resize when activity lands.
    if (v === 'activity') {
        return (
            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: `${BLOCK_GAP}px`,
                }}
            >
                <MarketBlock
                    event={event}
                    height={marketBlockHeight ?? ACTIVITY_MARKET_BLOCK_HEIGHT}
                    selectedMarketId={selectedMarketId}
                    onSelectMarket={onSelectMarket}
                    scoreByMarket={scoreByMarket}
                    accentColor={accentColor}
                    outcomes={outcomes}
                    outcomesByMarket={outcomesByMarket}
                    series={series}
                    seriesWindow={seriesWindow}
                    group={score?.group ?? null}
                    eventId={event.event_id}
                    now={t}
                />
                <ActivityBlock
                    ctx={activityCtx ?? PENDING_ACTIVITY}
                    marketLabel={activityMarketLabel}
                />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: `${BLOCK_GAP}px`,
            }}
        >
            <MarketBlock
                event={event}
                height={marketBlockHeight ?? MARKET_BLOCK_HEIGHT}
                selectedMarketId={selectedMarketId}
                onSelectMarket={onSelectMarket}
                scoreByMarket={scoreByMarket}
                accentColor={accentColor}
                outcomes={outcomes}
                outcomesByMarket={outcomesByMarket}
                series={series}
                seriesWindow={seriesWindow}
                group={score?.group ?? null}
                eventId={event.event_id}
                now={t}
            />
            <ConvictionRow
                score={score}
                variant={v}
                now={t}
                emptyText={scoreEmptyText}
                outcomes={outcomes}
                event={event}
            />
        </Box>
    );
}
